import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Users, Plus, User, Shield, CreditCard, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccountSidebar({ token }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [teams, setTeams] = useState([]);
    const [loadingTeams, setLoadingTeams] = useState(true);

    useEffect(() => {
        fetchTeams();
    }, [token]);

    const fetchTeams = async () => {
        try {
            const response = await axios.get(`${API}/api/teams`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeams(response.data);
        } catch (error) {
            console.error('Failed to fetch teams:', error);
        } finally {
            setLoadingTeams(false);
        }
    };

    const navItemClass = (path) =>
        `settings-nav-item ${pathname === path ? 'active' : ''}`;

    return (
        <div className="settings-sidebar">
            {/* Account Section */}
            <div className="account-sidebar-card">
                <div className="account-sidebar-label">Account</div>
                <nav className="settings-nav">
                    <Link to="/account" className={navItemClass('/account')}>
                        <User className="nav-icon" />
                        <span>Preferences</span>
                    </Link>
                    <Link to="/account/security" className={navItemClass('/account/security')}>
                        <Shield className="nav-icon" />
                        <span>Security</span>
                    </Link>
                    <Link to="/subscription" className={navItemClass('/subscription')}>
                        <CreditCard className="nav-icon" />
                        <span>Subscription</span>
                    </Link>
                    <Link to="/danger" className={navItemClass('/danger')}>
                        <AlertTriangle className="nav-icon" />
                        <span>Danger zone</span>
                    </Link>
                </nav>
            </div>

            {/* Teams Section */}
            <div className="account-sidebar-card account-sidebar-card--teams">
                <div className="account-sidebar-label-row">
                    <div className="account-sidebar-label">Teams</div>
                    <button
                        onClick={() => navigate('/teams')}
                        className="account-sidebar-add"
                        title="Manage teams"
                        aria-label="Manage teams"
                    >
                        <Plus className="w-3.5 h-3.5" />
                    </button>
                </div>
                <nav className="settings-nav">
                    {loadingTeams ? (
                        <div className="account-sidebar-empty">Loading...</div>
                    ) : teams.length > 0 ? (
                        teams.map(team => {
                            const canManageTeam = ['owner', 'admin'].includes(team.role);
                            const content = (
                                <>
                                    <Users className="nav-icon" />
                                    <span className="account-sidebar-team-name">{team.name}</span>
                                    <span className={`teams-row__role teams-row__role--${team.role}`}>
                                        {team.role}
                                    </span>
                                </>
                            );

                            return canManageTeam ? (
                                <Link
                                    key={team.id}
                                    to={`/team/${team.id}/settings`}
                                    className={navItemClass(`/team/${team.id}/settings`)}
                                >
                                    {content}
                                </Link>
                            ) : (
                                <div
                                    key={team.id}
                                    className="settings-nav-item account-sidebar-team-readonly"
                                >
                                    {content}
                                </div>
                            );
                        })
                    ) : (
                        <button
                            onClick={() => navigate('/teams')}
                            className="account-sidebar-cta"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Manage teams
                        </button>
                    )}
                </nav>
            </div>
        </div>
    );
}
